import React from 'react'
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import ErrorBoundary from './components/ErrorBoundary'
import ReferralCapture from './components/ReferralCapture'
import ChatbotWidget from './components/ChatbotWidget'
import BookAppointment from './pages/BookAppointment'
import LoginPage from './pages/Login'
import PatientDashboard from './pages/PatientDashboard'
import DoctorDashboard from './pages/DoctorDashboard'
import LabelingTool from './pages/LabelingTool'
import MedicationTraining from './components/MedicationTraining'
import PatientDetails from './pages/PatientDetails'
import RevokedTokens from './pages/RevokedTokens'
import Admins from './pages/Admins'
import AdminLogin from './pages/AdminLogin'
import AdminDashboard from './pages/AdminDashboard'
import SuperAdminDashboard from './pages/SuperAdminDashboard'
import About from './pages/About'
import { AuthProvider, useAuth } from './AuthContext'
import ContactPage from './pages/Contact'
import PrivacyPolicy from './pages/PrivacyPolicy'
import TermsOfService from './pages/TermsOfService'
import RefundPolicy from './pages/RefundPolicy'
import Services from './pages/Services'
import FollowUps from './pages/FollowUps'
import CartPage from './pages/Cart'
import CheckoutPage from './pages/Checkout'
import { getCart, addToCart as addToCartUtil, buyNow as buyNowUtil, removeFromCart as removeFromCartUtil, clearCart as clearCartUtil, saveCart as saveCartUtil } from './utils/cart'
import { isRx } from './utils/rx'
import PharmacyQueue from './pages/PharmacyQueue'
import DeleteAccount from './pages/DeleteAccount'
import { API_BASE } from './config'
import { ProductsProvider, useProducts } from './context/ProductsContext'
import ClinikallyShop from './pages/ClinikallyShop'
import SkinQuizPage from './pages/SkinQuizPage'
import WarmHome from './pages/WarmHome'
import { MobileShopHome, MobileShopList, MobileCategories, MobileConsult, MobileProfile, MobileTabBar, MobileProductDetail, MobileOrders, MobileConsults } from './mobile/MobileShop'
import MobileLogin from './mobile/MobileLogin'
import DoctorLoginGate from './mobile/DoctorLoginGate'
import { IS_DOCTOR_APP } from './appFlavor'
import { ScanFace, Scissors, Sun, Hourglass, Leaf, Zap, Droplet, Wind, Scale, Sparkles } from 'lucide-react'

// Native Capacitor app → app-like shell (bottom tabs, no site header/footer)
let _Cap = null
try { _Cap = require('@capacitor/core').Capacitor } catch (e) {}
// Test-only override: on localhost ONLY, ?native=1 forces the mobile app UI so
// automated E2E can render/click the patient mobile screens in a browser.
// Inert on production (guarded to localhost), so it can never affect real users.
const _forceNative = (() => {
  try {
    if (typeof window === 'undefined') return false
    const h = window.location.hostname
    if (h !== 'localhost' && h !== '127.0.0.1') return false
    const p = new URLSearchParams(window.location.search).get('native')
    if (p === '1') { try { localStorage.setItem('revive_force_native', '1') } catch (e) {} return true }
    if (p === '0') { try { localStorage.removeItem('revive_force_native') } catch (e) {} return false }
    return localStorage.getItem('revive_force_native') === '1'
  } catch (e) { return false }
})()
const IS_NATIVE_APP = _forceNative || !!(_Cap && _Cap.isNativePlatform && _Cap.isNativePlatform())

// Scroll to the top whenever the route (path or query) changes — otherwise a
// new screen keeps the previous scroll position and appears "in the middle".
function ScrollToTop() {
  const { pathname, search } = useLocation()
  React.useEffect(() => {
    try {
      window.scrollTo(0, 0)
      const m = document.querySelector('main')
      if (m && m.scrollTo) m.scrollTo(0, 0)
    } catch (e) {}
  }, [pathname, search])
  return null
}

// Native app: full-screen login gate — phone+OTP is mandatory before the app
function MobileLoginGate() {
  const { user } = useAuth()
  if (user?.token) return null
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: '#fff', overflowY: 'auto' }}>
      <MobileLogin />
    </div>
  )
}

// Native app: a signed-in doctor gets routed to their dashboard instead of the patient shop.
function DoctorShell() {
  const { user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  React.useEffect(() => {
    if (user?.role !== 'doctor') return
    const patientHome = ['/', '/shop', '/m/categories', '/m/consult', '/m/profile', '/m/orders', '/m/consults']
    if (patientHome.includes(location.pathname)) navigate('/doctor-dashboard', { replace: true })
  }, [user, location.pathname, navigate])
  return null
}
import StaffRegister from './pages/StaffRegister'
import ProductPage from './pages/ProductPage'
function ProductPageKeyed({ onAdd }) {
  const { id } = useParams()
  return <ProductPage key={id} onAdd={onAdd} />
}
import OrderComplete from './pages/OrderComplete'
import ViewCartPage from './pages/ViewCart'
import Aclaris from './pages/Aclaris'
import PharmacyRegister from './pages/PharmacyRegister'

// ── DESIGN TOKENS ──────────────────────────────────────────
const C = {
  g900:'#0D2218', g800:'#1A3A28', g700:'#235233', g600:'#2D6B42',
  g500:'#3D8A57', g400:'#5AAE73', g300:'#85C99A', g100:'#E8F5EE', g50:'#F2FAF5',
  white:'#FFFFFF', text:'#1A2820', muted:'#5A7065', border:'#C8DDD2',
  error:'#C0392B',
  headerGrad:'linear-gradient(150deg,#0F3D24 0%,#1E6B3C 60%,#2E8B4F 100%)',
}

const GCSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  html,body{max-width:100%;overflow-x:hidden}
  body{font-family:'DM Sans',sans-serif;background:#fff;color:#1A2820;-webkit-font-smoothing:antialiased;overflow-x:hidden}
  .hdr-burger{display:none}
  .hdr-mobile-login{display:none}
  .site-footer-grid{display:grid;grid-template-columns:2.5fr 1fr 1fr 1fr;gap:3rem}
  @media (max-width:900px){
    .hdr-nav{display:none!important}
    .hdr-actions{display:none!important}
    .hdr-burger{display:flex!important}
    .hdr-mobile-login{display:flex!important}
    .site-footer-grid{grid-template-columns:1fr 1fr;gap:1.75rem}
    /* Stack any two-column marketing rows so nothing gets clipped on phones */
    .hero-grid{grid-template-columns:1fr!important;gap:2rem!important}
    .resp-2col{grid-template-columns:1fr!important;gap:1.5rem!important}
    /* Shop: filter sidebar goes on top, full width, on mobile */
    .shop-row{flex-direction:column!important;gap:1.25rem!important}
    .shop-aside{width:100%!important}
  }
  @media (max-width:520px){
    .site-footer-grid{grid-template-columns:1fr;gap:1.5rem}
  }
  h1,h2,h3,h4,h5{font-family:'DM Serif Display',serif;font-weight:400}
  ::selection{background:#85C99A;color:#0D2218}
  ::-webkit-scrollbar{width:4px}
  ::-webkit-scrollbar-track{background:#F2FAF5}
  ::-webkit-scrollbar-thumb{background:#5AAE73;border-radius:2px}
  @keyframes fadeUp{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
  @keyframes marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.45}}
  .f1{animation:fadeUp .8s ease both}
  .f2{animation:fadeUp .8s .12s ease both}
  .f3{animation:fadeUp .8s .24s ease both}
  .f4{animation:fadeUp .8s .38s ease both}
  .float-anim{animation:float 6s ease-in-out infinite}
  .brand-card{transition:all 0.18s ease;cursor:pointer;}
  .brand-card:hover{transform:translateY(-3px);box-shadow:0 8px 24px rgba(0,0,0,0.1)!important;}
  .brand-letter-btn{transition:all 0.15s;}
  .brand-letter-btn:hover{background:#235233!important;color:white!important;}
`

const CATS = ['All','Sunscreen','Moisturizers','Face Wash','De-Pigmenting','Serums','Toners','Hair Care','Skincare']

const ALL_BRANDS = [
  "8X","ABBOTT","ACLARIS","ACNECHIO","ADONIS","AESTHETIC INSIGHTS","AETHICZ","AGERET",
  "AHAGLOW","AJANTA PHARMA","AKUMENTIS","ALEMBIC","ALKEM","ALNICHE","ALOPEL","ALTRIS",
  "APAISANT","AQUAOAT","AQUASOFT","AVEENO","AVEIL","AVEXA","AVROHN","AZELIA",
  "BABE","BB YAP","BEAUTYWISE","BELLACOS","BENZAC","BETA DRUGS","BIHANS PHARMACEUTICALS",
  "BILUMA","BIODERMA","BIOKENT HEALTHCARE","BIOPHARMACIEAA","BIORED","BIOSILK","BIOUV",
  "BLABON","BLU BLOC","BLYNDS","BONTRESS","BRINTON",
  "CANIXA","CANTABRIA LABS","CERATOP","CERAVE","CETAPHIL","CEUTICOZ","CIPLA",
  "COOLA","COSMELITE","COSMOFIX","COSMONOVA","CRYSTAL TOMATO","CUTICANS","CUTICAPIL","CUTILIFT","CUTIWASH",
  "DEMELAN","DERMA ANGEL","DERMACEUTIC","DERMAELITE","DERMATICA","DERMETICS",
  "DEWSILK","DEWSOFT","DHRUV MEDICOS","DR REDDY","DR REDDY'S","DUCRAY",
  "EFATOP","EL CLINICALS","EMBRYOLISSE","EMCURE","ENAGENBIO","EPICRAFT PHARMA",
  "EPILIGIUM","EPIQUE","EPISOFT","ERIS","ETHICARE","ETHIGLO","ETHINEXT PHARMA","EVANICO","EXCELA","EZANIC",
  "FACEMOIST","FAIR INSTA","FASH","FCL","FIXDERMA","FLAWLIZO","FOLIBLIS",
  "GALDERMA","GEOSMATIC","GLENMARK","GLOWDERMA","GLOWEVER","GLOWTIVA","GROGAIN","GUFIC",
  "HAIR 4U","HAIRIZER","HELIOCARE","HYBLOK","HYDEW","HYDRO",
  "INDOGRACE","INDOLANDS","INTAS","IPCA","IS CLINICAL","ISDIN",
  "KERABOOST","KERASTEM","KEVON","KOJIGLO",
  "LA PRISTINE","LA ROCHE-POSAY","LA SHIELD","LEAFON","LEXTRA","LIVALITE",
  "LOGIDRUF","LOGIFEEL","LOGIHAIR","LUMACIP","LUPIN",
  "MAXRICH","ME LINE","MEDISTE","MELALUMIN","MELANO-TX","MELAREAN","MELASOME",
  "MELBLOK","MEMBIO","MESODEW","MESOESTETIC","MESOVA","MHG","MINTOP","MIS D SUN","MOIZ","MORR",
  "NEUTROGENA","NOREVA","NOURRIR","NOVEGROW","NUMIS MED","NUSAUDE","NUTROVA",
  "OBAGI","OBRIL","OKNET","OLESOFT","ORYZA",
  "PALSONS","PELTOS","PERCOS","PEROBAR","PILGRIM",
  "RADIFFECT","RALYCOS","REGA-BPO","REGAINE","REGALIZ","REGIUN PHARMACEUTICALS","RENOCIA","RILASTIL","ROCKMED","ROOT-IN",
  "SALIGLY","SALISIA","SALVE","SANOSAN","SASLIC","SCALPE","SEBAMED","SEBOASAL","SEDGE",
  "SESDERMA","SKINMEDIS","SKINNOVATION NEXT","SKINTRA","SKINVERSE","SOLGLO","SUNBLESS","SUNPROTEK","SYNERGY","SYSCUTIS",
  "TORRENT PHARMA","TRICHOSPIRE","TRICLEAR","TRIJUV","TRIKONA","TRIOLITE","TRUDERMA","TUGAIN","TYRODIN",
  "ULTRA-V","URIAGE","UV DOUX","UVSKRIN",
  "VANIZA","VITSERA","WOCKHARDT","XTRA DENSER","YAP COSDERM","YUDERMA","ZARYWIN","ZITCARE","ZYDUS",
]

const BRAND_LOGOS = {
  "BIODERMA":"https://logo.clearbit.com/bioderma.com",
  "CETAPHIL":"https://logo.clearbit.com/cetaphil.com",
  "CERAVE":"https://logo.clearbit.com/cerave.com",
  "NEUTROGENA":"https://logo.clearbit.com/neutrogena.com",
  "AVEENO":"https://logo.clearbit.com/aveeno.com",
  "CIPLA":"https://logo.clearbit.com/cipla.com",
  "DR REDDY'S":"https://logo.clearbit.com/drreddys.com",
  "DR REDDY":"https://logo.clearbit.com/drreddys.com",
  "GALDERMA":"https://logo.clearbit.com/galderma.com",
  "SEBAMED":"https://logo.clearbit.com/sebamed.com",
  "URIAGE":"https://logo.clearbit.com/uriage.com",
  "DUCRAY":"https://logo.clearbit.com/ducray.com",
  "PILGRIM":"https://logo.clearbit.com/pilgrimbeauty.com",
  "OBAGI":"https://logo.clearbit.com/obagi.com",
  "FIXDERMA":"https://logo.clearbit.com/fixderma.com",
  "BIOSILK":"https://logo.clearbit.com/biosilk.com",
  "ISDIN":"https://logo.clearbit.com/isdin.com",
  "MESOESTETIC":"https://logo.clearbit.com/mesoestetic.com",
  "LUPIN":"https://logo.clearbit.com/lupinpharmaceuticals.com",
  "IPCA":"https://logo.clearbit.com/ipcalabs.com",
  "ABBOTT":"https://logo.clearbit.com/abbott.com",
  "GLENMARK":"https://logo.clearbit.com/glenmarkpharma.com",
  "EMCURE":"https://logo.clearbit.com/emcure.co.in",
  "INTAS":"https://logo.clearbit.com/intaspharma.com",
  "COOLA":"https://logo.clearbit.com/coolasurf.com",
  "EMBRYOLISSE":"https://logo.clearbit.com/embryolisse.com",
  "HELIOCARE":"https://logo.clearbit.com/heliocare.com",
  "NOREVA":"https://logo.clearbit.com/noreva.com",
  "BENZAC":"https://logo.clearbit.com/benzac.com",
  "SESDERMA":"https://logo.clearbit.com/sesderma.com",
  "RILASTIL":"https://logo.clearbit.com/rilastil.com",
}

function brandColor(name) {
  const colors = [
    {bg:'#F0FFF4',text:'#2D6B42',border:'#C8DDD2'},
    {bg:'#EFF6FF',text:'#1D4ED8',border:'#BFDBFE'},
    {bg:'#FFF7ED',text:'#C2410C',border:'#FED7AA'},
    {bg:'#FDF4FF',text:'#7E22CE',border:'#E9D5FF'},
    {bg:'#F0FDFA',text:'#0F766E',border:'#99F6E4'},
    {bg:'#FFF1F2',text:'#BE123C',border:'#FECDD3'},
    {bg:'#FEFCE8',text:'#A16207',border:'#FEF08A'},
    {bg:'#F0F9FF',text:'#0369A1',border:'#BAE6FD'},
  ]
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return colors[Math.abs(hash) % colors.length]
}

const s = {
  btnPrimary:{background:C.g700,color:'white',padding:'.55rem 1.4rem',borderRadius:8,fontSize:'.82rem',fontWeight:600,border:'none',cursor:'pointer',fontFamily:"'DM Sans',sans-serif",letterSpacing:'.03em',boxShadow:`0 2px 8px rgba(35,82,51,.25)`,transition:'all .2s',textDecoration:'none',display:'inline-flex',alignItems:'center',gap:7},
  btnOutline:{background:'white',border:`1px solid ${C.border}`,color:C.text,padding:'.5rem 1.1rem',borderRadius:8,fontSize:'.8rem',fontWeight:500,cursor:'pointer',fontFamily:"'DM Sans',sans-serif",transition:'all .2s'},
  btnGreen:{background:C.g400,color:C.g900,padding:'.85rem 2rem',borderRadius:10,fontSize:'.875rem',fontWeight:600,border:'none',cursor:'pointer',fontFamily:"'DM Sans',sans-serif",letterSpacing:'.04em',boxShadow:`0 4px 20px rgba(90,174,115,.35)`,transition:'all .2s',textDecoration:'none',display:'inline-flex',alignItems:'center',gap:8},
  btnGhost:{background:'rgba(255,255,255,.1)',color:'white',padding:'.85rem 2rem',borderRadius:10,fontSize:'.875rem',fontWeight:500,border:'1px solid rgba(255,255,255,.2)',cursor:'pointer',fontFamily:"'DM Sans',sans-serif",letterSpacing:'.04em',transition:'all .2s',textDecoration:'none',display:'inline-flex',alignItems:'center',gap:8},
  tag:{display:'inline-flex',alignItems:'center',gap:8,background:'rgba(255,255,255,.08)',border:'1px solid rgba(255,255,255,.15)',borderRadius:999,padding:'.35rem 1rem',marginBottom:'1.75rem'},
  eyebrow:{fontSize:'.68rem',letterSpacing:'.18em',textTransform:'uppercase',color:C.g600,marginBottom:'.65rem',display:'flex',alignItems:'center',gap:10},
  sectionTitle:{fontFamily:"'DM Serif Display',serif",fontSize:'clamp(1.8rem,3.5vw,2.75rem)',color:C.g900,lineHeight:1.15},
  inner:{maxWidth:1600,margin:'0 auto',padding:'0 2rem'},
}

export default function App() {
  const [cart, setCart] = React.useState(() => getCart())
  const [modal, setModal] = React.useState(null)
  const [toast, setToast] = React.useState(null)
  const [cartOpen, setCartOpen] = React.useState(false)

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2800) }
  const addToCart = (p) => { setCart(addToCartUtil(p)); showToast(`${p.name} added ✓`) }
  const removeFromCart = (p) => setCart(removeFromCartUtil(item => item.name === p.name))
  const clearCart = () => setCart(clearCartUtil())
  const updateQty = (item, delta) => setCart(prev => {
    const next = prev
      .map(i => (i.id === item.id || i.name === item.name) ? { ...i, quantity: (i.quantity || i.qty || 1) + delta } : i)
      .filter(i => (i.quantity ?? 1) > 0)   // 0 must drop the item — (0 || 1) wrongly kept it
    saveCartUtil(next)
    return next
  })

  return (
    <AuthProvider>
      <ProductsProvider>
      <Router>
        <ScrollToTop />
        <style>{GCSS}</style>
        {toast && (
          <div style={{position:'fixed',bottom:'2rem',left:'50%',transform:'translateX(-50%)',zIndex:800,background:C.g800,color:'white',padding:'.8rem 1.75rem',borderRadius:999,fontSize:'.83rem',fontWeight:500,boxShadow:'0 8px 28px rgba(13,34,24,.25)',display:'flex',alignItems:'center',gap:9,whiteSpace:'nowrap',animation:'fadeIn .3s ease',letterSpacing:'.01em'}}>
            <span style={{width:7,height:7,borderRadius:'50%',background:C.g300,flexShrink:0,display:'inline-block'}}/>
            {toast}
          </div>
        )}
        {modal && <ProductModal product={modal} onClose={() => setModal(null)} onAdd={(p) => { addToCart(p); setModal(null) }} />}
        <CartDrawer cart={cart} open={cartOpen} onClose={() => setCartOpen(false)} onRemove={removeFromCart} onClear={clearCart} onUpdateQty={updateQty} native={IS_NATIVE_APP} />
        <ConsentGate />
        <ReferralCapture />
        <div style={{minHeight:'100vh',display:'flex',flexDirection:'column'}}>
          {!IS_NATIVE_APP && <Header cartCount={cart.length} onCartOpen={() => setCartOpen(true)} />}
          <main style={{flex:1, paddingBottom: (IS_NATIVE_APP && !IS_DOCTOR_APP) ? 70 : 0}}>
            <ErrorBoundary>
            <Routes>
              <Route path="/" element={IS_DOCTOR_APP ? <DoctorDashboard /> : IS_NATIVE_APP ? <MobileShopHome onAdd={addToCart} cartCount={cart.length} onCartOpen={() => setCartOpen(true)} /> : <WarmHome />} />
              <Route path="/m/categories" element={<MobileCategories onAdd={addToCart} />} />
              <Route path="/m/consult" element={<MobileConsult />} />
              <Route path="/m/profile" element={<MobileProfile />} />
              <Route path="/m/orders" element={<MobileOrders />} />
              <Route path="/m/consults" element={<MobileConsults />} />
              <Route path="/shop" element={IS_NATIVE_APP ? <MobileShopList onAdd={addToCart} cartCount={cart.length} onCartOpen={() => setCartOpen(true)} /> : <ShopPage onOpen={setModal} onAdd={addToCart} />} />
              <Route path="/brands" element={<BrandsPage />} />
              <Route path="/skins" element={<SkinsPage />} />
              <Route path="/cart" element={<CartPage cart={cart} onRemove={removeFromCart} onClear={clearCart} />} />
              <Route path="/checkout" element={<CheckoutPage onClear={clearCart} />} />
              <Route path="/services" element={<Services />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/followups" element={<FollowUps />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/book-appointment" element={<BookAppointment />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/patient-dashboard" element={<PatientDashboard onAdd={addToCart} />} />
              <Route path="/doctor-dashboard" element={<DoctorDashboard />} />
              <Route path="/label" element={<LabelingTool />} />
              <Route path="/medication-training" element={<div style={{ maxWidth: 820, margin: '0 auto', padding: '24px 16px' }}><MedicationTraining /></div>} />
              <Route path="/patient/:email" element={<PatientDetails />} />
              <Route path="/admin/revoked" element={<RevokedTokens />} />
              <Route path="/admin/admins" element={<Admins />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<SuperAdminDashboard />} />
              <Route path="/admin/overview" element={<SuperAdminDashboard />} />
              <Route path="/admin/console" element={<AdminDashboard />} />
              <Route path="/product/:id" element={IS_NATIVE_APP ? <MobileProductDetail onAdd={addToCart} cartCount={cart.length} onCartOpen={() => setCartOpen(true)} /> : <ProductPageKeyed onAdd={addToCart} />} />
              <Route path="/shop/clinikally" element={<ClinikallyShop />} />
              <Route path="/skin-quiz" element={<SkinQuizPage />} />
              <Route path="/staff-register" element={<StaffRegister />} />
              <Route path="/register" element={<StaffRegister />} />
              <Route path="/order-confirmation/:oid" element={<OrderComplete />} />
              <Route path="/view-cart" element={<ViewCartPage />} />
              <Route path="/brands/aclaris" element={<Aclaris />} />
              <Route path="/pharmacy-register" element={<PharmacyRegister />} />
              <Route path="/pharmacy" element={<PharmacyQueue />} />
              <Route path="/delete-account" element={<DeleteAccount />} />
            </Routes>
            </ErrorBoundary>
          </main>
          {!IS_NATIVE_APP && <Footer />}
          {!IS_DOCTOR_APP && <ChatbotWidget bottomInset={IS_NATIVE_APP ? 78 : 0} />}
          {IS_NATIVE_APP && !IS_DOCTOR_APP && <MobileTabBar />}
          {IS_NATIVE_APP && !IS_DOCTOR_APP && <DoctorShell />}
          {IS_NATIVE_APP && <MobileInfoBack />}
          {IS_NATIVE_APP && !IS_DOCTOR_APP && <MobileLoginGate />}
          {IS_DOCTOR_APP && <DoctorLoginGate />}
        </div>
      </Router>
      </ProductsProvider>
    </AuthProvider>
  )
}

function Header({ cartCount, onCartOpen }) {
  const location = useLocation(), navigate = useNavigate(), auth = useAuth()
  const [scrolled, setScrolled] = React.useState(false)
  const [moreOpen, setMoreOpen] = React.useState(false)
  const [shopOpen, setShopOpen] = React.useState(false)
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const moreRef = React.useRef(null)
  const shopRef = React.useRef(null)

  React.useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', fn)
    return () => window.removeEventListener('scroll', fn)
  }, [])

  React.useEffect(() => {
    const fn = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false)
      if (shopRef.current && !shopRef.current.contains(e.target)) setShopOpen(false)
    }
    document.addEventListener('mousedown', fn)
    return () => document.removeEventListener('mousedown', fn)
  }, [])

  React.useEffect(() => { setMoreOpen(false); setShopOpen(false); setMobileOpen(false) }, [location.pathname])

  const moreLinks = [
    {to:'/about',label:'About Us',icon:'🏥',desc:'Our story, doctors & values'},
    {to:'/contact',label:'Contact',icon:'✉️',desc:'Get in touch with our team'},
    {to:'/pharmacy-register',label:'Partner with Us',icon:'🤝',desc:'Join as pharmacy or doctor'},
  ]
  const shopLinks = [
    {to:'/shop',label:'All Products',icon:'🛍️',desc:'Browse full collection'},
    {to:'/shop',label:'Sunscreen',icon:'☀️',desc:'SPF protection range'},
    {to:'/shop',label:'Moisturizers',icon:'💧',desc:'Hydration & barrier care'},
    {to:'/shop',label:'Serums',icon:'💎',desc:'Targeted treatments'},
    {to:'/shop',label:'Face Wash',icon:'🧴',desc:'Gentle cleansers'},
    {to:'/brands',label:'All Brands',icon:'🏷️',desc:'200+ trusted brands'},
  ]

  const isMoreActive = ['/about','/contact'].includes(location.pathname)
  const isShopActive = ['/shop','/brands'].includes(location.pathname)

  const NavLink = ({ to, label }) => {
    const active = location.pathname === to
    return (
      <Link to={to} style={{color:active?C.g700:C.muted,fontWeight:active?600:400,fontSize:'.85rem',textDecoration:'none',letterSpacing:'.02em',position:'relative',padding:'.5rem .85rem',borderRadius:8,display:'block',transition:'color .2s'}}
        onMouseEnter={e=>e.currentTarget.style.background=C.g50}
        onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
        {label}
        <span style={{position:'absolute',bottom:4,left:'50%',transform:`translateX(-50%) scaleX(${active?1:0})`,width:'calc(100% - 1.6rem)',height:2,background:C.g500,borderRadius:1,transition:'transform .25s',display:'block'}}/>
      </Link>
    )
  }

  const DropBtn = ({ label, isActive, isOpen, onClick }) => (
    <button onClick={onClick}
      style={{display:'flex',alignItems:'center',gap:5,color:isActive?C.g700:C.muted,fontWeight:isActive?600:400,fontSize:'.85rem',background:isOpen?C.g50:'transparent',border:'none',cursor:'pointer',padding:'.5rem .85rem',borderRadius:8,fontFamily:"'DM Sans',sans-serif",letterSpacing:'.02em',transition:'all .2s',position:'relative'}}
      onMouseEnter={e=>e.currentTarget.style.background=C.g50}
      onMouseLeave={e=>{if(!isOpen)e.currentTarget.style.background='transparent'}}>
      {label}
      <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{transition:'transform .2s',transform:isOpen?'rotate(180deg)':'rotate(0)'}}><polyline points="6 9 12 15 18 9"/></svg>
      <span style={{position:'absolute',bottom:4,left:'50%',transform:`translateX(-50%) scaleX(${isActive?1:0})`,width:'calc(100% - 1.6rem)',height:2,background:C.g500,borderRadius:1,display:'block'}}/>
    </button>
  )

  const DropItem = ({ to, label, icon, desc, onClose }) => (
    <Link to={to} onClick={onClose}
      style={{display:'flex',alignItems:'center',gap:12,padding:'.65rem .75rem',borderRadius:10,textDecoration:'none',transition:'background .15s',color:C.text,background:location.pathname===to?C.g100:'transparent'}}
      onMouseEnter={e=>e.currentTarget.style.background=C.g50}
      onMouseLeave={e=>e.currentTarget.style.background=location.pathname===to?C.g100:'transparent'}>
      <span style={{width:34,height:34,borderRadius:9,background:C.g100,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1rem',flexShrink:0}}>{icon}</span>
      <div>
        <div style={{fontSize:'.83rem',fontWeight:600,color:location.pathname===to?C.g700:C.g800}}>{label}</div>
        <div style={{fontSize:'.72rem',color:C.muted}}>{desc}</div>
      </div>
    </Link>
  )

  const Dropdown = ({ children, width=270 }) => (
    <div style={{position:'absolute',top:'calc(100% + 8px)',left:'50%',transform:'translateX(-50%)',background:'white',border:`1px solid ${C.border}`,borderRadius:16,boxShadow:'0 16px 48px rgba(13,34,24,.15)',padding:'.75rem',width,zIndex:300,animation:'fadeIn .15s ease'}}>
      {children}
    </div>
  )

  return (
    <header style={{position:'sticky',top:0,zIndex:200,background:'rgba(255,255,255,.97)',backdropFilter:'blur(16px)',borderBottom:`1px solid ${C.border}`,boxShadow:scrolled?'0 4px 24px rgba(13,34,24,.08)':'none',transition:'box-shadow .3s'}}>
      <div style={{...s.inner,display:'flex',alignItems:'center',height:72,gap:'1.5rem'}}>
        {/* Mobile hamburger — left side */}
        <button className="hdr-burger" onClick={()=>setMobileOpen(o=>!o)} aria-label="Menu"
          style={{alignItems:'center',justifyContent:'center',width:42,height:42,borderRadius:10,border:`1px solid ${C.border}`,background:C.g50,cursor:'pointer',flexShrink:0}}>
          <svg width="20" height="20" fill="none" stroke={C.g800} strokeWidth="2" viewBox="0 0 24 24">
            {mobileOpen
              ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
              : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
          </svg>
        </button>
        <Link to="/" style={{display:'flex',alignItems:'center',gap:12,textDecoration:'none',flexShrink:0}}>
          <div style={{width:40,height:40,borderRadius:10,background:`linear-gradient(135deg,${C.g700},${C.g500})`,display:'flex',alignItems:'center',justifyContent:'center'}}>
            <svg width="20" height="20" fill="none" stroke="white" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </div>
          <div>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:'1.3rem',color:C.g800}}>Revive</div>
            <div style={{fontSize:'.6rem',color:C.muted,letterSpacing:'.14em',textTransform:'uppercase',marginTop:1}}>Skin & Hair Experts</div>
          </div>
        </Link>
        <nav className="hdr-nav" style={{display:'flex',gap:'.1rem',alignItems:'center',flex:1,justifyContent:'center'}}>
          <NavLink to="/" label="Home"/>
          <NavLink to="/services" label="Services"/>
          <div ref={shopRef} style={{position:'relative'}}>
            <DropBtn label="Shop" isActive={isShopActive} isOpen={shopOpen} onClick={()=>{setShopOpen(o=>!o);setMoreOpen(false)}}/>
            {shopOpen && (
              <Dropdown width={290}>
                <div style={{fontSize:'.65rem',letterSpacing:'.14em',textTransform:'uppercase',color:C.muted,padding:'.3rem .75rem .5rem',fontWeight:600}}>Browse Products</div>
                {shopLinks.map(l=><DropItem key={l.label} {...l} onClose={()=>setShopOpen(false)}/>)}
              </Dropdown>
            )}
          </div>
          <NavLink to="/brands" label="Brands"/>
          <div ref={moreRef} style={{position:'relative'}}>
            <DropBtn label="More" isActive={isMoreActive} isOpen={moreOpen} onClick={()=>{setMoreOpen(o=>!o);setShopOpen(false)}}/>
            {moreOpen && (
              <Dropdown width={275}>
                <div style={{fontSize:'.65rem',letterSpacing:'.14em',textTransform:'uppercase',color:C.muted,padding:'.3rem .75rem .5rem',fontWeight:600}}>More Pages</div>
                {moreLinks.map(l=><DropItem key={l.to} {...l} onClose={()=>setMoreOpen(false)}/>)}
              </Dropdown>
            )}
          </div>
          <NavLink to="/skin-quiz" label="Skin Quiz"/>
        </nav>
        <div className="hdr-actions" style={{display:'flex',alignItems:'center',gap:'.65rem',flexShrink:0}}>
          {auth?.user ? (
            <>
              <Link to={auth.user.role==='doctor'?'/doctor-dashboard':auth.user.role==='admin'?'/admin/dashboard':'/patient-dashboard'}
                style={{fontSize:'.8rem',color:C.muted,textDecoration:'none',padding:'.4rem .75rem',borderRadius:8,transition:'all .2s'}}
                onMouseEnter={e=>{e.currentTarget.style.color=C.g700;e.currentTarget.style.background=C.g50}}
                onMouseLeave={e=>{e.currentTarget.style.color=C.muted;e.currentTarget.style.background='transparent'}}>
                Dashboard
              </Link>
              <button onClick={()=>{auth.logout();navigate('/')}} style={{...s.btnOutline}}>Logout</button>
            </>
          ) : (
            <Link to="/login" style={{...s.btnOutline,textDecoration:'none'}}>Login</Link>
          )}
          <Link to="/book-appointment" style={{...s.btnPrimary,padding:'.5rem 1.25rem',fontSize:'.8rem'}}>Book Consult</Link>
          <button onClick={onCartOpen}
            style={{display:'flex',alignItems:'center',gap:6,padding:'.45rem .9rem',borderRadius:8,border:`1px solid ${C.border}`,background:C.g50,cursor:'pointer',fontSize:'.8rem',color:C.text,fontFamily:"'DM Sans',sans-serif",transition:'all .2s'}}
            onMouseEnter={e=>{e.currentTarget.style.background=C.g100;e.currentTarget.style.borderColor=C.g400}}
            onMouseLeave={e=>{e.currentTarget.style.background=C.g50;e.currentTarget.style.borderColor=C.border}}>
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
            <span style={{background:C.g600,color:'white',borderRadius:'50%',width:18,height:18,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.62rem',fontWeight:600}}>{cartCount||0}</span>
          </button>
        </div>

        {/* Mobile login/account icon — right side (where the hamburger used to be) */}
        <Link className="hdr-mobile-login" aria-label={auth?.user ? 'Account' : 'Login'}
          to={auth?.user ? (auth.user.role==='doctor'?'/doctor-dashboard':auth.user.role==='admin'?'/admin/dashboard':'/patient-dashboard') : '/login'}
          style={{marginLeft:'auto',alignItems:'center',justifyContent:'center',width:42,height:42,borderRadius:10,border:`1px solid ${C.border}`,background:C.g50,color:C.g800,flexShrink:0,textDecoration:'none'}}>
          <svg width="21" height="21" fill="none" stroke="currentColor" strokeWidth="1.9" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        </Link>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div style={{borderTop:`1px solid ${C.border}`,background:'white',padding:'.75rem 1rem 1.25rem',display:'flex',flexDirection:'column',gap:'.15rem'}}>
          {[['/', 'Home'],['/services','Services'],['/shop','Shop'],['/brands','Brands'],['/skin-quiz','Skin Quiz'],['/about','About Us'],['/contact','Contact'],['/pharmacy-register','Partner with Us']].map(([to,label])=>(
            <Link key={label} to={to} onClick={()=>setMobileOpen(false)}
              style={{padding:'.7rem .5rem',borderRadius:8,textDecoration:'none',color:location.pathname===to?C.g700:C.text,fontWeight:location.pathname===to?600:500,fontSize:'.95rem'}}>
              {label}
            </Link>
          ))}
          <div style={{display:'flex',gap:'.6rem',marginTop:'.75rem'}}>
            {auth?.user ? (
              <>
                <Link to={auth.user.role==='doctor'?'/doctor-dashboard':auth.user.role==='admin'?'/admin/dashboard':'/patient-dashboard'} onClick={()=>setMobileOpen(false)}
                  style={{...s.btnOutline,textDecoration:'none',flex:1,textAlign:'center'}}>Dashboard</Link>
                <button onClick={()=>{auth.logout();setMobileOpen(false);navigate('/')}} style={{...s.btnOutline,flex:1}}>Logout</button>
              </>
            ) : (
              <Link to="/login" onClick={()=>setMobileOpen(false)} style={{...s.btnOutline,textDecoration:'none',flex:1,textAlign:'center'}}>Login</Link>
            )}
          </div>
          <div style={{display:'flex',gap:'.6rem',marginTop:'.6rem'}}>
            <Link to="/book-appointment" onClick={()=>setMobileOpen(false)} style={{...s.btnPrimary,padding:'.6rem 1rem',fontSize:'.85rem',flex:1,textAlign:'center',textDecoration:'none'}}>Book Consult</Link>
            <button onClick={()=>{setMobileOpen(false);onCartOpen()}} style={{...s.btnOutline,flex:1}}>Cart ({cartCount||0})</button>
          </div>
        </div>
      )}
    </header>
  )
}

// Native app: floating back button on info/legal pages (they have no header there)
function MobileInfoBack() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const showOn = ['/privacy-policy', '/terms-of-service', '/refund-policy', '/contact']
  if (!showOn.includes(pathname)) return null
  return (
    <button onClick={() => navigate(-1)} style={{ position: 'fixed', top: 12, left: 12, zIndex: 950, width: 40, height: 40, borderRadius: 12, background: 'rgba(255,255,255,.95)', border: `1px solid ${C.border}`, boxShadow: '0 2px 10px rgba(0,0,0,.12)', fontSize: '1.5rem', color: C.g900, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>‹</button>
  )
}

// DPDP consent — shown once per patient (bump version to re-prompt on policy change)
const CONSENT_VERSION = '2026-07'
function ConsentGate() {
  const { user, fetchWithAuth } = useAuth()
  const [show, setShow] = React.useState(false)
  React.useEffect(() => {
    if (!user?.token) { setShow(false); return }
    if (['admin', 'doctor', 'pharmacist'].includes(user.role)) return
    try { if (localStorage.getItem('revive_consent_' + CONSENT_VERSION)) return } catch {}
    setShow(true)
  }, [user])
  if (!show) return null
  const agree = async () => {
    try { localStorage.setItem('revive_consent_' + CONSENT_VERSION, '1') } catch {}
    try { await fetchWithAuth(`${API_BASE}/api/consent`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ version: CONSENT_VERSION }) }) } catch {}
    setShow(false)
  }
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 980, background: 'rgba(13,34,24,.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div style={{ background: '#fff', width: '100%', maxWidth: 520, borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: '1.6rem 1.3rem 2rem', boxShadow: '0 -10px 40px rgba(0,0,0,.2)' }}>
        <div style={{ fontSize: '1.8rem', marginBottom: 8 }}>🔒</div>
        <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: '1.35rem', color: C.g900, marginBottom: 10 }}>Your privacy &amp; consent</div>
        <div style={{ fontSize: '.85rem', color: C.text, lineHeight: 1.6, marginBottom: 12 }}>
          To provide your care, Revive collects and processes your personal and health information — photos, diagnoses, prescriptions and lifestyle logs. We use it <b>only</b> to deliver and improve your treatment, and we never sell your data.
        </div>
        <div style={{ fontSize: '.8rem', color: C.muted, lineHeight: 1.6, marginBottom: 18 }}>
          By continuing you agree to our <Link to="/privacy-policy" style={{ color: C.g700, fontWeight: 700 }}>Privacy Policy</Link> and consent to the processing of your health data. You can withdraw consent anytime by contacting us.
        </div>
        <button onClick={agree} style={{ width: '100%', background: C.g700, color: '#fff', border: 'none', borderRadius: 12, padding: '.95rem', fontWeight: 800, fontSize: '.95rem', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>I Agree &amp; Continue</button>
      </div>
    </div>
  )
}

function CartDrawer({ cart, open, onClose, onRemove, onClear, onUpdateQty, native }) {
  const navigate = useNavigate()
  React.useEffect(() => { console.log('CartDrawer open:', open) }, [open])
  const qtyOf = i => i.quantity || i.qty || 1
  const total = (cart||[]).reduce((s,i)=>s+(i.price||0)*qtyOf(i),0)
  const count = (cart||[]).reduce((s,i)=>s+qtyOf(i),0)
  const W = native ? '100vw' : 420
  const cld = (url, size=140) => (url && url.includes('/upload/')) ? url.replace('/upload/', `/upload/e_trim/c_pad,b_white,w_${size},h_${size},q_auto,f_auto/`) : url
  const bottomPad = native ? 32 : 20  // above the phone gesture bar (drawer covers the tab bar)
  return (
    <>
      {open && <div onClick={onClose} style={{position:'fixed',inset:0,zIndex:940,background:'rgba(13,34,24,.45)',backdropFilter:'blur(4px)'}}/>}
      <div style={{position:'fixed',top:0,right:open?0:(typeof W==='number'?`-${W+20}px`:'-101vw'),width:W,maxWidth:'100vw',height:'100vh',background:'#fff',zIndex:950,boxShadow:'-8px 0 40px rgba(13,34,24,.15)',transition:'right .35s cubic-bezier(.4,0,.2,1)',display:'flex',flexDirection:'column'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:native?'2.6rem 1.15rem 1rem':'1.5rem 1.75rem',borderBottom:`1px solid ${C.border}`,background:C.g50}}>
          <div style={{fontFamily:"'DM Serif Display',serif",fontSize:'1.4rem',color:C.g900}}>Your Cart{count>0?` · ${count}`:''}</div>
          <button onClick={()=>{ console.log('Close button clicked'); onClose() }} style={{width:36,height:36,borderRadius:10,border:`1px solid ${C.border}`,background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:C.muted,fontSize:'1.1rem'}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:'1rem 1.15rem'}}>
          {!cart.length ? (
            <div style={{textAlign:'center',padding:'3.5rem 0',color:C.muted}}>
              <div style={{fontSize:'2.5rem',marginBottom:'1rem',opacity:.2}}>🛒</div>
              <p style={{fontWeight:600,color:C.text}}>Your cart is empty</p>
              <p style={{fontSize:'.8rem',marginTop:'.4rem'}}>Add products to get started</p>
              <button onClick={()=>{onClose();navigate('/shop')}} style={{marginTop:'1.4rem',background:C.g700,color:'#fff',border:'none',borderRadius:999,padding:'.7rem 1.6rem',fontWeight:700,fontSize:'.85rem',cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}>Browse products</button>
            </div>
          ) : cart.map((it,i)=>(
            <div key={i} style={{display:'flex',gap:12,padding:'.9rem 0',borderBottom:`1px solid ${C.border}`,alignItems:'stretch'}}>
              <div style={{width:62,height:62,borderRadius:12,background:'#fff',border:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.5rem',flexShrink:0,overflow:'hidden'}}>
                {it.imgs?.[0]?<img src={cld(it.imgs[0])} alt={it.name} style={{width:'100%',height:'100%',objectFit:'contain'}}/>:<span>{it.emoji||'🧴'}</span>}
              </div>
              <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8}}>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:'.85rem',fontWeight:600,color:C.g900,lineHeight:1.3,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{it.name}</div>
                    {isRx(it) && <span style={{display:'inline-block',fontSize:'.54rem',fontWeight:700,color:'#B7770D',background:'#FFF8E1',border:'1px solid #FFE082',borderRadius:6,padding:'.1rem .4rem',marginTop:3}}>℞ Prescription</span>}
                  </div>
                  <button onClick={()=>onRemove(it)} aria-label="Remove" style={{background:'none',border:'none',color:C.muted,cursor:'pointer',fontSize:'1.05rem',padding:2,flexShrink:0,lineHeight:1}}>✕</button>
                </div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:'auto',paddingTop:8}}>
                  {/* qty stepper */}
                  <div style={{display:'flex',alignItems:'center',border:`1px solid ${C.border}`,borderRadius:999,overflow:'hidden'}}>
                    <button onClick={()=>onUpdateQty(it,-1)} style={{width:30,height:30,border:'none',background:'#fff',color:C.g700,fontSize:'1.1rem',cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}>−</button>
                    <span style={{minWidth:22,textAlign:'center',fontSize:'.85rem',fontWeight:700,color:C.g900}}>{qtyOf(it)}</span>
                    <button onClick={()=>onUpdateQty(it,1)} style={{width:30,height:30,border:'none',background:'#fff',color:C.g700,fontSize:'1.05rem',cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}>+</button>
                  </div>
                  <div style={{fontWeight:800,fontSize:'.98rem',color:C.g800}}>₹{(it.price*qtyOf(it)).toLocaleString('en-IN')}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {cart.length>0 && (
          <div style={{padding:`1.1rem 1.15rem ${bottomPad}px`,borderTop:`1px solid ${C.border}`,background:C.g50}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'.9rem'}}>
              <span style={{fontSize:'.85rem',color:C.muted}}>Order Total</span>
              <span style={{fontFamily:"'DM Serif Display',serif",fontSize:'1.6rem',color:C.g800}}>₹{total.toLocaleString('en-IN')}</span>
            </div>
            <button onClick={()=>{onClose();navigate('/checkout',{state:{cart}})}} style={{width:'100%',padding:'.95rem',borderRadius:12,background:C.g700,color:'#fff',border:'none',fontSize:'.9rem',fontWeight:700,cursor:'pointer',fontFamily:"'DM Sans',sans-serif",letterSpacing:'.03em',marginBottom:'.5rem'}}>
              Proceed to Checkout
            </button>
            <button onClick={onClear} style={{width:'100%',background:'none',border:'none',color:C.muted,fontSize:'.75rem',cursor:'pointer',fontFamily:"'DM Sans',sans-serif",padding:'.3rem'}}>Clear all items</button>
          </div>
        )}
      </div>
    </>
  )
}

function ProductModal({ product, onClose, onAdd }) {
  const [zoom, setZoom]       = React.useState(false)
  const [pos, setPos]         = React.useState({ x:50, y:50 })
  const [hovered, setHovered] = React.useState(false)
  const boxRef = React.useRef(null)
  const hasImg = !!product.imgs?.[0]

  const onMouseMove = (e) => {
    if (!boxRef.current) return
    const r = boxRef.current.getBoundingClientRect()
    setPos({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 })
  }

  return (
    <>
      {/* FULLSCREEN ZOOM OVERLAY */}
      {zoom && (
        <div onClick={()=>setZoom(false)} style={{position:'fixed',inset:0,zIndex:700,background:'rgba(0,0,0,.93)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'zoom-out',animation:'fadeIn .2s ease'}}>
          <img src={product.imgs[0]} alt={product.name} style={{maxWidth:'90vw',maxHeight:'90vh',objectFit:'contain',borderRadius:14,boxShadow:'0 40px 100px rgba(0,0,0,.7)',animation:'fadeUp .25s ease'}} onClick={e=>e.stopPropagation()} />
          <button onClick={()=>setZoom(false)} style={{position:'absolute',top:'1.5rem',right:'1.5rem',width:44,height:44,borderRadius:12,background:'rgba(255,255,255,.1)',border:'1px solid rgba(255,255,255,.2)',color:'white',fontSize:'1.2rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(8px)',transition:'all .2s'}}
            onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,.2)'}
            onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,.1)'}>✕</button>
          <div style={{position:'absolute',bottom:'1.75rem',left:'50%',transform:'translateX(-50%)',color:'rgba(255,255,255,.35)',fontSize:'.72rem',letterSpacing:'.1em',whiteSpace:'nowrap'}}>Click anywhere to close</div>
        </div>
      )}

      <div onClick={e=>e.target===e.currentTarget&&onClose()} style={{position:'fixed',inset:0,zIndex:500,background:'rgba(13,34,24,.6)',backdropFilter:'blur(10px)',display:'flex',alignItems:'center',justifyContent:'center',padding:'1.25rem'}}>
        <div style={{background:'white',borderRadius:24,maxWidth:740,width:'100%',maxHeight:'92vh',overflowY:'auto',animation:'fadeUp .3s ease',border:`1px solid ${C.border}`,boxShadow:'0 32px 80px rgba(13,34,24,.25)'}}>

          {/* Header */}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'1.25rem 1.75rem',borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
            <span style={{fontSize:'.65rem',letterSpacing:'.14em',textTransform:'uppercase',color:C.g500,fontWeight:600,background:C.g100,padding:'.25rem .75rem',borderRadius:999}}>{product.cat}</span>
            <button onClick={onClose} style={{width:34,height:34,borderRadius:9,border:`1px solid ${C.border}`,background:C.g50,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:C.muted,fontSize:'.95rem',transition:'all .2s',flexShrink:0}}
              onMouseEnter={e=>{e.currentTarget.style.background=C.g100;e.currentTarget.style.borderColor=C.g400}}
              onMouseLeave={e=>{e.currentTarget.style.background=C.g50;e.currentTarget.style.borderColor=C.border}}>✕</button>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1.15fr'}}>

            {/* IMAGE PANEL with hover-zoom + fullscreen */}
            <div ref={boxRef}
              style={{position:'relative',background:`linear-gradient(160deg,${C.g50},${C.g100})`,display:'flex',alignItems:'center',justifyContent:'center',padding:'2.5rem 2rem',borderRight:`1px solid ${C.border}`,minHeight:340,overflow:'hidden',cursor:hasImg?'crosshair':'default'}}
              onMouseMove={hasImg ? onMouseMove : undefined}
              onMouseEnter={()=>setHovered(true)}
              onMouseLeave={()=>setHovered(false)}>

              {hasImg ? (
                <>
                  {/* Base image */}
                  <img src={product.imgs[0]} alt={product.name} style={{maxWidth:'100%',maxHeight:340,objectFit:'contain',borderRadius:12,display:'block',position:'relative',zIndex:1}} />

                  {/* Magnifier layer */}
                  <div style={{
                    position:'absolute',inset:0,zIndex:2,
                    backgroundImage:`url(${product.imgs[0]})`,
                    backgroundSize:'280%',
                    backgroundPosition:`${pos.x}% ${pos.y}%`,
                    backgroundRepeat:'no-repeat',
                    opacity: hovered ? 1 : 0,
                    transition:'opacity .12s',
                    pointerEvents:'none',
                  }} />

                  {/* Fullscreen zoom button */}
                  <button onClick={()=>setZoom(true)}
                    style={{position:'absolute',bottom:12,right:12,zIndex:3,width:36,height:36,borderRadius:10,background:'rgba(255,255,255,.92)',border:`1px solid ${C.border}`,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1rem',boxShadow:'0 2px 10px rgba(13,34,24,.15)',transition:'all .2s'}}
                    title="View full size"
                    onMouseEnter={e=>{e.currentTarget.style.background=C.g700;e.currentTarget.style.color='white';e.currentTarget.style.borderColor=C.g700}}
                    onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,.92)';e.currentTarget.style.color='inherit';e.currentTarget.style.borderColor=C.border}}>
                    🔍
                  </button>

                  {/* Hover hint */}
                  {!hovered && (
                    <div style={{position:'absolute',top:12,left:12,zIndex:3,fontSize:'.62rem',background:'rgba(255,255,255,.85)',color:C.muted,padding:'.25rem .65rem',borderRadius:999,backdropFilter:'blur(4px)',letterSpacing:'.05em',pointerEvents:'none'}}>
                      Hover to zoom
                    </div>
                  )}
                </>
              ) : (
                <span style={{fontSize:'5rem',opacity:.12}}>🧴</span>
              )}
            </div>

            {/* INFO */}
            <div style={{display:'flex',flexDirection:'column',padding:'2rem',gap:'1.1rem'}}>
              <h2 style={{fontFamily:"'DM Serif Display',serif",fontSize:'1.5rem',color:C.g900,lineHeight:1.3,margin:0}}>{product.name}</h2>

              <div style={{display:'flex',alignItems:'baseline',gap:8}}>
                <span style={{fontFamily:"'DM Serif Display',serif",fontSize:'2.1rem',color:C.g700,lineHeight:1}}>₹{product.price}</span>
                <span style={{fontSize:'.72rem',color:C.muted}}>incl. taxes</span>
              </div>

              {product.desc && (
                <div style={{background:C.g50,borderRadius:12,padding:'1rem 1.1rem',border:`1px solid ${C.border}`}}>
                  <div style={{fontSize:'.62rem',letterSpacing:'.12em',textTransform:'uppercase',color:C.g500,fontWeight:700,marginBottom:'.5rem'}}>About</div>
                  <p style={{fontSize:'.83rem',color:'#374151',lineHeight:1.9,margin:0}}>{product.desc}</p>
                </div>
              )}

              {product.bullets?.length > 0 && (
                <ul style={{listStyle:'none',margin:0,padding:0,display:'flex',flexDirection:'column',gap:'.35rem'}}>
                  {product.bullets.map((b,i) => (
                    <li key={i} style={{display:'flex',alignItems:'center',gap:9,fontSize:'.82rem',color:C.text,padding:'.35rem 0',borderBottom:`1px solid ${C.border}`}}>
                      <span style={{width:6,height:6,borderRadius:'50%',background:C.g500,flexShrink:0,display:'inline-block'}} />{b}
                    </li>
                  ))}
                </ul>
              )}

              <div style={{display:'flex',gap:'.65rem',paddingTop:'.25rem',marginTop:'auto'}}>
                <button onClick={()=>onAdd(product)}
                  style={{flex:1,padding:'.9rem',borderRadius:10,background:C.g700,color:'white',border:'none',fontSize:'.8rem',fontWeight:600,cursor:'pointer',fontFamily:"'DM Sans',sans-serif",letterSpacing:'.04em',transition:'all .2s',boxShadow:`0 4px 16px rgba(35,82,51,.3)`}}
                  onMouseEnter={e=>e.currentTarget.style.background=C.g800}
                  onMouseLeave={e=>e.currentTarget.style.background=C.g700}>
                  🛒 ADD TO CART
                </button>
                <button onClick={onClose}
                  style={{padding:'.9rem 1.1rem',borderRadius:10,background:'white',color:C.g700,border:`1.5px solid ${C.border}`,fontSize:'.8rem',fontWeight:600,cursor:'pointer',fontFamily:"'DM Sans',sans-serif",transition:'all .2s'}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=C.g400;e.currentTarget.style.background=C.g50}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.background='white'}}>
                  CLOSE
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  )
}


function ProductCard({ product, onAdd }) {
  const navigate = useNavigate()
  const [hov, setHov] = React.useState(false)
  const [adding, setAdding] = React.useState(false)
  const mrp = product.mrp || Math.round((product.price || 0) * 1.3)
  const savings = product.price && mrp > product.price ? Math.round(((mrp - product.price) / mrp) * 100) : 0
  const imgs = product.imgs?.length ? product.imgs : []

  const handleAdd = (e) => {
    e.stopPropagation()
    onAdd(product)
    setAdding(true)
    setTimeout(() => setAdding(false), 1800)
  }

  return (
    <div onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)} onClick={()=>navigate(`/product/${product.id}`)}
      style={{background:'white',borderRadius:14,overflow:'hidden',cursor:'pointer',border:`1px solid ${hov?C.g300:'#eee'}`,boxShadow:hov?'0 8px 28px rgba(45,107,66,.1)':'0 1px 4px rgba(0,0,0,.05)',transition:'all .25s',transform:hov?'translateY(-4px)':'none',display:'flex',flexDirection:'column'}}>
      {/* Square image area */}
      <div style={{position:'relative',background:'white',paddingTop:'100%'}}>
        <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          {imgs.length>0
            ?<img src={imgs[0]} alt={product.name} style={{width:'100%',height:'100%',objectFit:'contain',mixBlendMode:'multiply',transition:'transform .3s',transform:hov?'scale(1.07)':'scale(1)'}}/>
            :<span style={{fontSize:'3.5rem',opacity:.15}}>{product.emoji||'🧴'}</span>}
        </div>
        {savings>=5&&<span style={{position:'absolute',top:10,left:10,background:'#e53e3e',color:'white',fontSize:'.62rem',fontWeight:700,padding:'3px 8px',borderRadius:6,zIndex:2}}>{savings}% OFF</span>}
        {product.badge&&<span style={{position:'absolute',top:savings>=5?34:10,left:10,background:C.g700,color:'white',fontSize:'.58rem',fontWeight:600,padding:'3px 9px',borderRadius:999,letterSpacing:'.07em',zIndex:2}}>{product.badge.toUpperCase()}</span>}
      </div>
      {/* Info */}
      <div style={{padding:'12px 14px 14px',flex:1,display:'flex',flexDirection:'column',gap:4,borderTop:'1px solid #f3f4f6'}}>
        <div style={{fontSize:'.6rem',letterSpacing:'.1em',textTransform:'uppercase',color:C.g500,fontWeight:700}}>{product.cat}</div>
        <div style={{fontSize:'.85rem',fontWeight:600,color:'#1a1a1a',lineHeight:1.3,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden',flex:1}}>{product.name}</div>
        <div style={{display:'flex',alignItems:'baseline',gap:6,marginTop:2}}>
          <span style={{fontFamily:"'DM Serif Display',serif",fontSize:'1.15rem',color:'#111',fontWeight:700}}>₹{product.price}</span>
          {mrp>product.price&&<span style={{fontSize:'.75rem',color:'#9CA3AF',textDecoration:'line-through'}}>₹{mrp}</span>}
        </div>
        <button onClick={handleAdd} style={{marginTop:8,width:'100%',padding:'.6rem',borderRadius:8,background:adding?C.g600:C.g900,color:'white',border:'none',fontSize:'.78rem',fontWeight:700,cursor:'pointer',fontFamily:"'DM Sans',sans-serif",letterSpacing:'.04em',transition:'background .2s'}}>
          {adding?'✓ Added':'Add to Cart'}
        </button>
      </div>
    </div>
  )
}

function HomePage({ onOpen, onAdd }) {
  return (
    <>
      <HeroSection/>
      <MarqueeStrip/>
      <HowItWorks/>
      <ServicesSection/>
      <FeaturedProducts onOpen={onOpen} onAdd={onAdd}/>
      <SkinConcerns/>
      <DoctorsSection/>
      <TestimonialsSection/>
      <CTABanner/>
    </>
  )
}

function HeroSection() {
  const navigate = useNavigate()
  return (
    <section style={{minHeight:'62vh',padding:'2.5rem 0',display:'flex',alignItems:'center',background:`linear-gradient(145deg,${C.g900} 0%,${C.g800} 40%,${C.g700} 75%,${C.g600} 100%)`,position:'relative',overflow:'hidden'}}>
      <div style={{position:'absolute',inset:0,backgroundImage:`radial-gradient(circle, rgba(255,255,255,.025) 1px, transparent 1px)`,backgroundSize:'28px 28px',pointerEvents:'none'}}/>
      <div style={{position:'absolute',right:'-10%',top:'-10%',width:700,height:700,borderRadius:'50%',background:`radial-gradient(circle,rgba(93,174,115,.15) 0%,transparent 65%)`,pointerEvents:'none'}}/>
      <div style={{position:'absolute',left:'-5%',bottom:'-5%',width:500,height:500,borderRadius:'50%',background:`radial-gradient(circle,rgba(61,138,87,.1) 0%,transparent 60%)`,pointerEvents:'none'}}/>
      <div className="hero-grid" style={{...s.inner,display:'grid',gridTemplateColumns:'1fr 1fr',gap:'4rem',alignItems:'center',position:'relative',zIndex:1,width:'100%'}}>
        <div>
          <div className="f1" style={{...s.tag}}>
            <span style={{width:7,height:7,borderRadius:'50%',background:C.g300,display:'inline-block',animation:'pulse 2s ease-in-out infinite'}}/>
            <span style={{fontSize:'.72rem',color:'rgba(255,255,255,.75)',letterSpacing:'.14em',textTransform:'uppercase'}}>Reviewed by real dermatologists</span>
          </div>
          <h1 className="f2" style={{fontFamily:"'DM Serif Display',serif",fontSize:'clamp(2rem,4vw,3rem)',color:'white',lineHeight:1.08,marginBottom:'1rem'}}>
            Your skin, seen by a<br/>real <em style={{color:C.g300,fontStyle:'italic'}}>dermatologist</em>
          </h1>
          <p className="f3" style={{fontSize:'.95rem',color:'rgba(255,255,255,.65)',lineHeight:1.65,marginBottom:'1.5rem',fontWeight:300,maxWidth:440}}>
            Start with a free skin check, then get a personalised plan from a certified dermatologist — with medically-curated products delivered to your door. Consult online from ₹500.
          </p>
          <div className="f4" style={{display:'flex',gap:'1rem',flexWrap:'wrap'}}>
            <button onClick={()=>navigate('/book-appointment')} style={{...s.btnGreen}}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              Book Consultation
            </button>
            <Link to="/shop" style={{...s.btnGhost}}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/></svg>
              Shop Products
            </Link>
          </div>
        </div>
        <div className="float-anim" style={{background:'rgba(255,255,255,.08)',backdropFilter:'blur(12px)',border:'1px solid rgba(255,255,255,.12)',borderRadius:24,padding:'2rem'}}>
          <div style={{fontSize:'.68rem',letterSpacing:'.14em',textTransform:'uppercase',color:'rgba(255,255,255,.45)',marginBottom:'1.5rem'}}>Quick Consult</div>
          <div style={{background:'white',borderRadius:16,padding:'1.5rem',marginBottom:'1rem'}}>
            <div style={{fontSize:'.68rem',letterSpacing:'.1em',textTransform:'uppercase',color:C.muted,marginBottom:'.5rem'}}>Online Consultation</div>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:'1.25rem',color:C.g800,marginBottom:'.75rem'}}>Expert Dermatologist</div>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'.5rem'}}>
              <span style={{display:'inline-flex',alignItems:'center',gap:6,background:C.g100,color:C.g700,padding:'.25rem .75rem',borderRadius:999,fontSize:'.78rem',fontWeight:600}}>Just ₹500</span>
              <span style={{fontSize:'.72rem',color:C.muted}}>via Google Meet</span>
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'.75rem'}}>
            {[['MD','Certified'],['200+','Brands'],['Free','Skin check']].map(([n,l])=>(
              <div key={l} style={{background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.1)',borderRadius:12,padding:'.9rem',textAlign:'center'}}>
                <div style={{fontFamily:"'DM Serif Display',serif",fontSize:'1.5rem',color:C.g300}}>{n}</div>
                <div style={{fontSize:'.65rem',color:'rgba(255,255,255,.4)',letterSpacing:'.08em',textTransform:'uppercase',marginTop:2}}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{position:'absolute',bottom:0,left:0,right:0,height:60,background:`linear-gradient(to bottom,transparent,white)`,pointerEvents:'none'}}/>
    </section>
  )
}

function MarqueeStrip() {
  const items = ['Expert Dermatologists','◆','200+ Brands','◆','Personalised Care','◆','Online Consultations','◆','₹500 Consult','◆','Trusted Care','◆','Fast Delivery','◆','Evidence-Based','◆']
  const doubled = [...items,...items]
  return (
    <div style={{background:C.g800,padding:'.8rem 0',overflow:'hidden',borderBottom:`1px solid rgba(255,255,255,.05)`}}>
      <div style={{display:'flex',animation:'marquee 30s linear infinite',width:'max-content'}}>
        {doubled.map((item,i)=>(
          <span key={i} style={{fontSize:'.72rem',letterSpacing:'.12em',textTransform:'uppercase',padding:'0 2.5rem',whiteSpace:'nowrap',color:item==='◆'?C.g300:'rgba(255,255,255,.55)',fontWeight:item==='◆'?400:300}}>{item}</span>
        ))}
      </div>
    </div>
  )
}

function HowItWorks() {
  const steps = [
    {n:'1',title:'Share Your Concern',desc:'Fill a short form with your skin or hair concerns and choose your preferred time.'},
    {n:'2',title:'Meet Your Doctor',desc:'Join a 15–20 min Google Meet consultation with a certified dermatologist.'},
    {n:'3',title:'Get Your Plan',desc:'Receive a personalised treatment plan and prescription for medical-grade products.'},
    {n:'4',title:'Delivered to You',desc:'Dermatologist-recommended products delivered straight to your doorstep.'},
  ]
  return (
    <section style={{background:C.g50,padding:'5.5rem 0'}}>
      <div style={{...s.inner}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-end',marginBottom:'3rem',flexWrap:'wrap',gap:'1rem'}}>
          <div>
            <div style={{...s.eyebrow}}><span style={{display:'inline-block',width:28,height:1.5,background:C.g500}}/>Simple Process</div>
            <h2 style={{...s.sectionTitle}}>How It Works</h2>
          </div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:'2rem'}}>
          {steps.map(st=>(
            <div key={st.n} style={{textAlign:'center'}}>
              <div style={{width:72,height:72,borderRadius:'50%',background:C.g700,color:'white',fontFamily:"'DM Serif Display',serif",fontSize:'1.5rem',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 1.25rem',boxShadow:`0 4px 16px rgba(45,107,66,.3)`}}>{st.n}</div>
              <div style={{fontSize:'.95rem',fontWeight:600,color:C.g900,marginBottom:'.5rem'}}>{st.title}</div>
              <div style={{fontSize:'.8rem',color:C.muted,lineHeight:1.7}}>{st.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function ServicesSection() {
  const services = [
    {name:'Acne Treatment',desc:'Medical-grade acne management and scar treatment.',Icon:ScanFace},
    {name:'Hair Loss',desc:'Advanced hair fall control and transplant solutions.',Icon:Scissors},
    {name:'Pigmentation',desc:'Targeted melasma and dark spot correction.',Icon:Sun},
    {name:'Anti-Ageing',desc:'Rejuvenation therapies for youthful, firm skin.',Icon:Hourglass},
    {name:'Eczema & Psoriasis',desc:'Specialist chronic skin condition management.',Icon:Leaf},
    {name:'Laser Treatments',desc:'Advanced laser for skin resurfacing and clarity.',Icon:Zap},
  ]
  return (
    <section style={{padding:'5.5rem 0'}}>
      <div style={{...s.inner}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-end',marginBottom:'3.5rem',flexWrap:'wrap',gap:'1rem'}}>
          <div>
            <div style={{...s.eyebrow}}><span style={{display:'inline-block',width:28,height:1.5,background:C.g500}}/>What We Treat</div>
            <h2 style={{...s.sectionTitle}}>Skin & Hair Services</h2>
          </div>
          <Link to="/services" style={{fontSize:'.8rem',color:C.g700,textDecoration:'none',borderBottom:`1.5px solid ${C.g400}`,paddingBottom:2}}>View all services →</Link>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:'1.25rem'}}>
          {services.map(sv=><ServiceCard key={sv.name} {...sv}/>)}
        </div>
      </div>
    </section>
  )
}

function ServiceCard({ name, desc, Icon }) {
  const [hov,setHov] = React.useState(false)
  return (
    <Link to="/book-appointment" style={{textDecoration:'none'}}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}>
      <div style={{background:hov?`linear-gradient(135deg,${C.g700},${C.g500})`:C.g50,border:`1px solid ${hov?C.g500:C.border}`,borderRadius:18,padding:'1.75rem 1.5rem',transition:'all .35s',cursor:'pointer',boxShadow:hov?`0 12px 36px rgba(45,107,66,.18)`:'none',transform:hov?'translateY(-4px)':'none'}}>
        <div style={{width:48,height:48,borderRadius:12,background:hov?'rgba(255,255,255,.15)':C.g100,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:'1.25rem',transition:'all .35s'}}>{Icon && <Icon size={22} strokeWidth={1.75} color={hov?'#fff':C.g700} />}</div>
        <div style={{fontSize:'.95rem',fontWeight:600,color:hov?'white':C.g800,marginBottom:'.5rem',transition:'color .35s'}}>{name}</div>
        <div style={{fontSize:'.8rem',color:hov?'rgba(255,255,255,.7)':C.muted,lineHeight:1.65,transition:'color .35s'}}>{desc}</div>
      </div>
    </Link>
  )
}

function FeaturedProducts({ onOpen, onAdd }) {
  const { products } = useProducts()
  const featured = products.filter(p => { const u = p.imgs?.[0]; return u && String(u).trim() && String(u) !== 'null' }).slice(0,4)
  return (
    <section style={{background:C.g50,padding:'5.5rem 0'}}>
      <div style={{...s.inner}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-end',marginBottom:'3.5rem',flexWrap:'wrap',gap:'1rem'}}>
          <div>
            <div style={{...s.eyebrow}}><span style={{display:'inline-block',width:28,height:1.5,background:C.g500}}/>Curated Products</div>
            <h2 style={{...s.sectionTitle}}>Featured Products</h2>
          </div>
          <Link to="/shop" style={{fontSize:'.8rem',color:C.g700,textDecoration:'none',borderBottom:`1.5px solid ${C.g400}`,paddingBottom:2}}>View all products →</Link>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:'1.5rem'}}>
          {featured.map(p=><ProductCard key={p.id} product={p} onOpen={onOpen} onAdd={onAdd}/>)}
        </div>
      </div>
    </section>
  )
}

function SkinConcerns() {
  const navigate = useNavigate()
  const concerns = [
    {Icon:Droplet,name:'Oily Skin',sub:'Oil-control care'},
    {Icon:Wind,name:'Dry Skin',sub:'Deep hydration'},
    {Icon:Leaf,name:'Sensitive',sub:'Gentle formulas'},
    {Icon:Scale,name:'Combination',sub:'Balance & care'},
    {Icon:Sun,name:'Dull Skin',sub:'Glow & radiance'},
    {Icon:ScanFace,name:'Acne-Prone',sub:'Blemish control'},
    {Icon:Sparkles,name:'Pigmentation',sub:'Even skin tone'},
    {Icon:Hourglass,name:'Anti-Ageing',sub:'Rejuvenate skin'},
  ]
  return (
    <section style={{padding:'5.5rem 0'}}>
      <div style={{...s.inner}}>
        <div style={{marginBottom:'3.5rem'}}>
          <div style={{...s.eyebrow}}><span style={{display:'inline-block',width:28,height:1.5,background:C.g500}}/>Find Your Solution</div>
          <h2 style={{...s.sectionTitle}}>Shop by Skin Type</h2>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))',gap:'1rem'}}>
          {concerns.map(c=><ConcernCard key={c.name} {...c} onClick={()=>navigate('/shop')}/>)}
        </div>
      </div>
    </section>
  )
}

function ConcernCard({ Icon, name, sub, onClick }) {
  const [hov,setHov] = React.useState(false)
  return (
    <div onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)} onClick={onClick}
      style={{border:`1.5px solid ${hov?C.g700:C.border}`,borderRadius:14,padding:'1.5rem 1.25rem',textAlign:'center',cursor:'pointer',transition:'all .3s',background:hov?C.g700:'white',transform:hov?'translateY(-3px)':'none',boxShadow:hov?`0 8px 24px rgba(45,107,66,.2)`:'none'}}>
      <span style={{display:'flex',justifyContent:'center',marginBottom:'.75rem'}}>{Icon && <Icon size={26} strokeWidth={1.6} color={hov?'#fff':C.g600} />}</span>
      <div style={{fontSize:'.85rem',fontWeight:600,color:hov?'white':C.g800,marginBottom:'.25rem',transition:'color .3s'}}>{name}</div>
      <div style={{fontSize:'.72rem',color:hov?'rgba(255,255,255,.6)':C.muted,transition:'color .3s'}}>{sub}</div>
    </div>
  )
}

function DoctorsSection() {
  const doctors = [
    {initial:'L',name:'Dr. Lakshmi Priya',qual:'MD, Dermatology. Dedicated to evidence-based skin & hair care, with personalised treatment plans for every patient.',tag:'Dermatologist',bg:`linear-gradient(135deg,${C.g800},${C.g600})`},
  ]
  return (
    <section style={{background:C.g50,padding:'5.5rem 0'}}>
      <div style={{...s.inner}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-end',marginBottom:'3.5rem',flexWrap:'wrap',gap:'1rem'}}>
          <div>
            <div style={{...s.eyebrow}}><span style={{display:'inline-block',width:28,height:1.5,background:C.g500}}/>Our Experts</div>
            <h2 style={{...s.sectionTitle}}>Meet the Doctors</h2>
          </div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:'2rem'}}>
          {doctors.map(d=><DoctorCard key={d.name} {...d}/>)}
        </div>
      </div>
    </section>
  )
}

function DoctorCard({ initial, name, qual, tag, bg }) {
  const [hov,setHov] = React.useState(false)
  return (
    <div onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{background:'white',border:`1px solid ${C.border}`,borderRadius:20,overflow:'hidden',transition:'all .3s',boxShadow:hov?'0 16px 56px rgba(13,34,24,.14)':'none',transform:hov?'translateY(-4px)':'none'}}>
      <div style={{height:190,background:bg,display:'flex',alignItems:'flex-end',justifyContent:'flex-start',padding:'1.5rem',position:'relative',overflow:'hidden'}}>
        <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'DM Serif Display',serif",fontSize:'8rem',color:'white',opacity:.07}}>{initial}</div>
        <div style={{width:64,height:64,borderRadius:'50%',background:'rgba(255,255,255,.15)',border:'2px solid rgba(255,255,255,.3)',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'DM Serif Display',serif",fontSize:'1.6rem',color:'white',position:'relative',zIndex:1}}>{initial}</div>
      </div>
      <div style={{padding:'1.5rem'}}>
        <div style={{fontFamily:"'DM Serif Display',serif",fontSize:'1.2rem',color:C.g900,marginBottom:'.35rem'}}>{name}</div>
        <div style={{fontSize:'.75rem',color:C.muted,lineHeight:1.65,marginBottom:'1rem'}}>{qual}</div>
        <span style={{display:'inline-flex',alignItems:'center',gap:6,background:C.g100,color:C.g700,padding:'.3rem .85rem',borderRadius:999,fontSize:'.72rem',fontWeight:600}}>✦ {tag}</span>
      </div>
    </div>
  )
}

function TestimonialsSection() {
  const items = [
    {init:'P',name:'Priya S.',label:'Acne & Pigmentation',text:"Revive stands out for its spot-on recommendations. They perfectly categorize products by skin type and concern. Every product I've bought has worked wonders."},
    {init:'R',name:'Rahul M.',label:'Hair Loss Treatment',text:"Dr. Lakshmi Priya is brilliant. Her recommendations and consultative approach have helped us immensely. The online consulting session works perfectly!"},
    {init:'A',name:'Asha K.',label:'Skincare Consultation',text:"Dr. Lakshmi Priya helped me clear my acne and regain my confidence. I'm beyond grateful — kind and compassionate. Truly transformed my skin."},
  ]
  return (
    <section style={{padding:'5.5rem 0'}}>
      <div style={{...s.inner}}>
        <div style={{marginBottom:'3.5rem'}}>
          <div style={{...s.eyebrow}}><span style={{display:'inline-block',width:28,height:1.5,background:C.g500}}/>Patient Stories</div>
          <h2 style={{...s.sectionTitle}}>What Our Patients Say</h2>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(270px,1fr))',gap:'1.5rem'}}>
          {items.map((it,i)=>(
            <div key={i} style={{background:'white',border:`1px solid ${C.border}`,borderRadius:18,padding:'2rem',position:'relative',overflow:'hidden',transition:'all .3s'}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=C.g400;e.currentTarget.style.boxShadow='0 4px 24px rgba(13,34,24,.08)'}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.boxShadow='none'}}>
              <div style={{position:'absolute',top:'1rem',right:'1.5rem',fontFamily:"'DM Serif Display',serif",fontSize:'5rem',color:C.g100,lineHeight:1}}>"</div>
              <div style={{color:C.g500,fontSize:'.85rem',marginBottom:'1rem',letterSpacing:'.08em'}}>★★★★★</div>
              <p style={{fontSize:'.875rem',color:C.muted,lineHeight:1.8,marginBottom:'1.5rem',position:'relative',zIndex:1}}>{it.text}</p>
              <div style={{display:'flex',alignItems:'center',gap:'.85rem'}}>
                <div style={{width:38,height:38,borderRadius:'50%',background:C.g700,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.95rem',color:'white',fontWeight:600}}>{it.init}</div>
                <div>
                  <div style={{fontSize:'.875rem',fontWeight:600,color:C.g900}}>{it.name}</div>
                  <div style={{fontSize:'.7rem',color:C.muted}}>{it.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function CTABanner() {
  const navigate = useNavigate()
  return (
    <section style={{background:C.g50,padding:'5.5rem 0'}}>
      <div style={{...s.inner}}>
        <div style={{background:`linear-gradient(130deg,${C.g900},${C.g700})`,borderRadius:24,padding:'4rem',display:'flex',justifyContent:'space-between',alignItems:'center',gap:'2rem',flexWrap:'wrap'}}>
          <div>
            <h2 style={{fontFamily:"'DM Serif Display',serif",fontSize:'clamp(1.75rem,3vw,2.5rem)',color:'white',marginBottom:'.75rem',lineHeight:1.15}}>Ready to transform<br/>your skin & hair?</h2>
            <p style={{color:'rgba(255,255,255,.6)',fontSize:'.9rem',maxWidth:420,lineHeight:1.75}}>Consult our certified dermatologists online from just ₹500. Get a personalised treatment plan and curated products delivered to your door.</p>
          </div>
          <div style={{display:'flex',gap:'1rem',flexShrink:0,flexWrap:'wrap'}}>
            <button onClick={()=>navigate('/book-appointment')} style={{...s.btnGreen}}>Book Consultation — ₹500</button>
            <Link to="/shop" style={{...s.btnGhost}}>Shop Products</Link>
          </div>
        </div>
      </div>
    </section>
  )
}

// ── SHOP PAGE — supports arriving from BrandsPage with brandFilter state ──
function ShopPage({ onOpen, onAdd }) {
  const { products: ALL_PRODUCTS, loading: productsLoading, error: productsError } = useProducts()
  const location = useLocation()
  const [brandFilter, setBrandFilter] = React.useState(location.state?.brandFilter || '')
  const [cat, setCat] = React.useState('All')
  const [search, setSearch] = React.useState('')
  const [sort, setSort] = React.useState('')
  const [maxPrice, setMaxPrice] = React.useState(5000)

  React.useEffect(() => {
    if (location.state?.brandFilter) { setBrandFilter(location.state.brandFilter); setCat('All'); setSearch('') }
  }, [location.state?.brandFilter])

  const allPrices = ALL_PRODUCTS.map(p => p.price).filter(Boolean)
  const globalMax = allPrices.length ? Math.ceil(Math.max(...allPrices) / 100) * 100 : 5000

  const filtered = React.useMemo(() => {
    // Hide products without a real photo so the grid never shows blank cards
    let items = ALL_PRODUCTS.filter(p => { const u = p.imgs?.[0]; return u && String(u).trim() && String(u) !== 'null' })
    if (brandFilter) items = items.filter(p => p.brand && p.brand.toUpperCase() === brandFilter.toUpperCase())
    if (cat !== 'All') items = items.filter(p => p.cat === cat)
    if (search) items = items.filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
    items = items.filter(p => !p.price || p.price <= maxPrice)
    if (sort === 'name') items = [...items].sort((a,b) => a.name.localeCompare(b.name))
    if (sort === 'price-asc') items = [...items].sort((a,b) => a.price - b.price)
    if (sort === 'price-desc') items = [...items].sort((a,b) => b.price - a.price)
    if (sort === 'rating') items = [...items].sort((a,b) => (b.rating||0) - (a.rating||0))
    return items
  }, [ALL_PRODUCTS, cat, search, sort, brandFilter, maxPrice])

  return (
    <div style={{background:'white',minHeight:'80vh',paddingTop:'2rem',paddingBottom:'4rem'}}>
      <div style={{...s.inner}}>
        <div style={{marginBottom:'1.5rem'}}>
          <div style={{...s.eyebrow}}><span style={{display:'inline-block',width:28,height:1.5,background:C.g500}}/>Our Collection</div>
          <h2 style={{...s.sectionTitle}}>Shop Products</h2>
        </div>

        {brandFilter && (
          <div style={{display:'flex',alignItems:'center',gap:'.75rem',marginBottom:'1.25rem'}}>
            <div style={{display:'inline-flex',alignItems:'center',gap:8,background:C.g100,border:`1.5px solid ${C.g400}`,borderRadius:999,padding:'.35rem .9rem',fontSize:'.8rem',fontWeight:600,color:C.g700}}>
              🏷️ {brandFilter}
              <button onClick={()=>setBrandFilter('')} style={{background:'none',border:'none',color:C.g600,cursor:'pointer',fontSize:'.9rem',lineHeight:1,padding:'0 0 0 2px',display:'flex',alignItems:'center'}}>✕</button>
            </div>
            <span style={{fontSize:'.78rem',color:C.muted}}>{filtered.length} products</span>
          </div>
        )}

        {/* Search + Sort bar */}
        <div style={{display:'flex',gap:'1rem',marginBottom:'1.5rem',flexWrap:'wrap',alignItems:'center'}}>
          <div style={{position:'relative',flex:1,minWidth:220}}>
            <span style={{position:'absolute',left:'1rem',top:'50%',transform:'translateY(-50%)',color:C.muted}}>🔍</span>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search products…"
              style={{width:'100%',padding:'.75rem 1rem .75rem 2.75rem',border:`1px solid ${C.border}`,borderRadius:9,fontSize:'.875rem',outline:'none',background:'white',boxSizing:'border-box',fontFamily:"'DM Sans',sans-serif"}}/>
          </div>
          <select value={sort} onChange={e=>setSort(e.target.value)}
            style={{padding:'.75rem 1.25rem',border:`1px solid ${C.border}`,borderRadius:9,fontSize:'.875rem',background:'white',cursor:'pointer',outline:'none',fontFamily:"'DM Sans',sans-serif",color:C.text}}>
            <option value="">Sort by</option>
            <option value="name">Name A–Z</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
            <option value="rating">Top Rated</option>
          </select>
          <span style={{fontSize:'.8rem',color:C.muted,whiteSpace:'nowrap'}}>{filtered.length} products</span>
        </div>

        {/* Sidebar + Grid */}
        <div className="shop-row" style={{display:'flex',gap:'2rem',alignItems:'flex-start'}}>
          {/* Sidebar */}
          <div className="shop-aside" style={{width:210,flexShrink:0}}>
            <div style={{marginBottom:'2rem'}}>
              <div style={{fontSize:'.72rem',fontWeight:700,letterSpacing:'.1em',textTransform:'uppercase',color:C.g800,marginBottom:'1rem',paddingBottom:'.5rem',borderBottom:`1.5px solid ${C.border}`}}>Type of Product</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:'.5rem'}}>
                {CATS.map(c=>{
                  const active=cat===c
                  return (
                    <button key={c} onClick={()=>setCat(c)}
                      style={{padding:'.45rem .9rem',borderRadius:999,border:`1.5px solid ${active?C.g700:C.border}`,background:active?C.g700:'#fff',color:active?'#fff':C.muted,fontSize:'.8rem',fontWeight:active?700:500,cursor:'pointer',fontFamily:"'DM Sans',sans-serif",whiteSpace:'nowrap',transition:'all .15s'}}>
                      {c}
                    </button>
                  )
                })}
              </div>
            </div>
            <div>
              <div style={{fontSize:'.72rem',fontWeight:700,letterSpacing:'.1em',textTransform:'uppercase',color:C.g800,marginBottom:'1rem',paddingBottom:'.5rem',borderBottom:`1.5px solid ${C.border}`}}>Price</div>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:'.77rem',color:C.muted,marginBottom:'.4rem'}}>
                <span>₹0</span><span>₹{maxPrice.toLocaleString()}</span>
              </div>
              <input type="range" min={0} max={globalMax} step={100} value={maxPrice} onChange={e=>setMaxPrice(Number(e.target.value))}
                style={{width:'100%',accentColor:C.g700,cursor:'pointer'}}/>
              <div style={{fontSize:'.77rem',color:C.g700,fontWeight:600,marginTop:'.3rem'}}>Up to ₹{maxPrice.toLocaleString()}</div>
            </div>
          </div>

          {/* Grid */}
          <div style={{flex:1}}>
            {productsLoading
              ? <div style={{textAlign:'center',padding:'4rem',color:C.muted}}>Loading products…</div>
              : productsError
                ? <div style={{textAlign:'center',padding:'4rem',background:'#FEF2F2',borderRadius:12,border:'1px solid #FCA5A5'}}>
                    <div style={{color:'#DC2626',fontWeight:600,marginBottom:'.5rem'}}>Could not load products</div>
                    <div style={{color:'#EF4444',fontSize:'.85rem',marginBottom:'1rem'}}>{productsError}</div>
                    <div style={{color:'#6B7280',fontSize:'.8rem'}}>Make sure the backend server is running on port 4001, then <button onClick={()=>window.location.reload()} style={{color:C.g700,background:'none',border:'none',cursor:'pointer',fontWeight:600,textDecoration:'underline'}}>refresh</button>.</div>
                  </div>
                : filtered.length===0
                  ? <div style={{textAlign:'center',padding:'4rem',color:C.muted}}>No products found.</div>
                  : <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:'1rem'}}>
                      {filtered.map(p=><ProductCard key={p.id} product={p} onAdd={onAdd}/>)}
                    </div>
            }
          </div>
        </div>
      </div>
    </div>
  )
}

// ── BRANDS PAGE — clicking a brand navigates to /shop with brand filter ──
function BrandsPage() {
  const navigate = useNavigate()
  const [search, setSearch] = React.useState('')
  const [activeLetter, setActiveLetter] = React.useState(null)

  const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
  const availableLetters = React.useMemo(()=>new Set(ALL_BRANDS.map(b=>b[0].toUpperCase())),[])

  const filtered = React.useMemo(()=>{
    return ALL_BRANDS.filter(b=>{
      const matchSearch = !search || b.toLowerCase().includes(search.toLowerCase())
      const matchLetter = !activeLetter || b.toUpperCase().startsWith(activeLetter)
      return matchSearch && matchLetter
    })
  },[search,activeLetter])

  const grouped = React.useMemo(()=>{
    const g = {}
    for (const b of filtered) {
      const L = b[0].toUpperCase()
      if (!g[L]) g[L] = []
      g[L].push(b)
    }
    return g
  },[filtered])

  return (
    <div style={{minHeight:'100vh',background:'#F8FAF9'}}>
      <div style={{background:C.headerGrad,padding:'1.9rem 2rem 1.6rem',textAlign:'center',position:'relative',overflow:'hidden'}}>
        <div style={{position:'absolute',top:-60,right:-40,width:190,height:190,borderRadius:'50%',background:'radial-gradient(circle,rgba(133,201,154,.22),transparent 70%)',pointerEvents:'none'}}/>
        <div style={{position:'relative',zIndex:1}}>
          <div style={{fontSize:'.62rem',letterSpacing:'.2em',textTransform:'uppercase',color:'rgba(255,255,255,.45)',marginBottom:'.35rem'}}>Revive Skin & Hair</div>
          <h1 style={{fontFamily:"'DM Serif Display',serif",fontSize:'clamp(1.4rem,3vw,2rem)',color:'white',margin:'0 0 .5rem'}}>Our Brands</h1>
          <p style={{color:'rgba(255,255,255,.55)',fontSize:'.85rem',margin:'0 0 1.1rem',maxWidth:480,marginLeft:'auto',marginRight:'auto'}}>
            {ALL_BRANDS.length}+ trusted dermatologist-recommended brands — click any brand to browse its products
          </p>
          <div style={{maxWidth:420,margin:'0 auto',position:'relative'}}>
            <span style={{position:'absolute',left:'1rem',top:'50%',transform:'translateY(-50%)',color:'rgba(255,255,255,.4)',fontSize:'1rem'}}>🔍</span>
            <input value={search} onChange={e=>{setSearch(e.target.value);setActiveLetter(null)}} placeholder="Search brands…"
              style={{width:'100%',padding:'.75rem 1rem .75rem 2.75rem',borderRadius:12,border:'1.5px solid rgba(255,255,255,.15)',background:'rgba(255,255,255,.08)',color:'white',fontSize:'.9rem',outline:'none',fontFamily:"'DM Sans',sans-serif",boxSizing:'border-box',backdropFilter:'blur(8px)'}}/>
          </div>
        </div>
      </div>

      <div style={{background:'white',borderBottom:`1px solid ${C.border}`,padding:'.65rem 1.5rem',position:'sticky',top:72,zIndex:10,overflowX:'auto'}}>
        <div style={{display:'flex',gap:4,justifyContent:'center',flexWrap:'wrap',maxWidth:900,margin:'0 auto'}}>
          <button className="brand-letter-btn" onClick={()=>{setActiveLetter(null);setSearch('')}}
            style={{padding:'.3rem .65rem',borderRadius:7,border:`1.5px solid ${!activeLetter?C.g700:C.border}`,background:!activeLetter?C.g700:'white',color:!activeLetter?'white':C.muted,fontSize:'.7rem',fontWeight:700,cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}>
            ALL
          </button>
          {LETTERS.map(L=>(
            <button key={L} className="brand-letter-btn"
              onClick={()=>{if(availableLetters.has(L)){setActiveLetter(activeLetter===L?null:L);setSearch('')}}}
              style={{width:32,height:32,borderRadius:7,border:`1.5px solid ${activeLetter===L?C.g700:C.border}`,background:activeLetter===L?C.g700:availableLetters.has(L)?'white':'#F3F4F6',color:activeLetter===L?'white':availableLetters.has(L)?C.text:C.border,fontSize:'.72rem',fontWeight:700,cursor:availableLetters.has(L)?'pointer':'default',fontFamily:"'DM Sans',sans-serif",display:'flex',alignItems:'center',justifyContent:'center'}}>
              {L}
            </button>
          ))}
        </div>
      </div>

      <div style={{maxWidth:1300,margin:'0 auto',padding:'2rem 1.5rem 4rem'}}>
        {Object.keys(grouped).length===0 ? (
          <div style={{textAlign:'center',padding:'5rem',color:C.muted}}>
            <div style={{fontSize:'3rem',opacity:.2,marginBottom:'1rem'}}>🔍</div>
            <div style={{fontSize:'1rem',fontWeight:600}}>No brands found for "{search}"</div>
          </div>
        ) : Object.keys(grouped).sort().map(letter=>(
          <div key={letter} style={{marginBottom:'2.5rem'}}>
            <div style={{display:'flex',alignItems:'center',gap:'1rem',marginBottom:'1rem'}}>
              <div style={{width:42,height:42,borderRadius:10,background:`linear-gradient(135deg,${C.g900},${C.g700})`,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'DM Serif Display',serif",fontSize:'1.2rem',color:'white',flexShrink:0}}>{letter}</div>
              <div style={{flex:1,height:1,background:`linear-gradient(to right,${C.border},transparent)`}}/>
              <span style={{fontSize:'.7rem',color:C.muted,fontWeight:600}}>{grouped[letter].length} brand{grouped[letter].length!==1?'s':''}</span>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))',gap:'.75rem'}}>
              {grouped[letter].map(brand=>{
                const logoUrl = BRAND_LOGOS[brand]
                const c = brandColor(brand)
                return (
                  <div key={brand} className="brand-card"
                    onClick={()=>navigate('/shop',{state:{brandFilter:brand}})}
                    style={{background:logoUrl?'white':c.bg,border:`1.5px solid ${logoUrl?C.border:c.border}`,borderRadius:12,padding:'1rem .75rem',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'.4rem',minHeight:88,boxShadow:'0 1px 4px rgba(0,0,0,0.05)'}}>
                    {logoUrl ? (
                      <>
                        <img src={logoUrl} alt={brand} style={{maxWidth:'100%',maxHeight:36,objectFit:'contain'}}
                          onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='block'}}/>
                        <div style={{display:'none',fontSize:'.68rem',fontWeight:700,color:C.g700,textAlign:'center'}}>{brand}</div>
                        <div style={{fontSize:'.6rem',color:C.muted,textAlign:'center',marginTop:2}}>{brand}</div>
                      </>
                    ) : (
                      <div style={{fontSize:'.72rem',fontWeight:700,color:c.text,textAlign:'center',lineHeight:1.3,letterSpacing:'.04em'}}>{brand}</div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      <div style={{textAlign:'center',padding:'1.5rem',borderTop:`1px solid ${C.border}`,color:C.muted,fontSize:'.8rem'}}>
        Showing {filtered.length} of {ALL_BRANDS.length} brands
      </div>
    </div>
  )
}

function SkinsPage() {
  return (
    <section style={{padding:'5rem 0',minHeight:'80vh'}}>
      <div style={{...s.inner}}>
        <h2 style={{...s.sectionTitle,marginBottom:'2.5rem'}}>Skin Treatments</h2>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:'1.5rem'}}>
          {['Acne Care','Pigmentation','Anti-ageing','Hair Loss','Eczema','Psoriasis'].map(t=>(
            <div key={t} style={{background:C.g50,border:`1px solid ${C.border}`,borderRadius:16,padding:'2rem',cursor:'pointer',transition:'all .3s'}}
              onMouseEnter={e=>{e.currentTarget.style.background=C.g100;e.currentTarget.style.borderColor=C.g400;e.currentTarget.style.transform='translateY(-3px)'}}
              onMouseLeave={e=>{e.currentTarget.style.background=C.g50;e.currentTarget.style.borderColor=C.border;e.currentTarget.style.transform='none'}}>
              <div style={{fontFamily:"'DM Serif Display',serif",fontSize:'1.1rem',color:C.g900}}>{t}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer style={{background:C.g900,color:'rgba(255,255,255,.45)',padding:'4.5rem 0 2rem'}}>
      <div style={{...s.inner}}>
        <div className="site-footer-grid" style={{marginBottom:'3.5rem'}}>
          <div>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:'1.25rem'}}>
              <div style={{width:36,height:36,borderRadius:10,background:'rgba(90,174,115,.2)',border:'1px solid rgba(90,174,115,.3)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                <svg width="18" height="18" fill="none" stroke={C.g300} strokeWidth="1.8" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </div>
              <span style={{fontFamily:"'DM Serif Display',serif",fontSize:'1.2rem',color:'white'}}>Revive</span>
            </div>
            <p style={{fontSize:'.83rem',lineHeight:1.85,maxWidth:260,marginBottom:'1.5rem'}}>One stop solution for all your skin and hair needs. Expert dermatologists, curated products, personalised care.</p>
            <div style={{display:'inline-flex',alignItems:'center',gap:8,background:'rgba(90,174,115,.15)',border:'1px solid rgba(90,174,115,.25)',color:C.g300,padding:'.4rem 1rem',borderRadius:8,fontSize:'.78rem',fontWeight:500}}>⚡ Consult now — just ₹500</div>
          </div>
          {[
            {title:'Navigate',links:[['/', 'Home'],['/shop','Shop'],['/services','Services'],['/about','About Us']]},
            {title:'Products',links:[['/shop','Sunscreen'],['/shop','Moisturizers'],['/shop','Face Wash'],['/brands','All Brands']]},
            {title:'Support',links:[['/contact','Contact Us'],['/book-appointment','Book Consult'],['/login','Patient Login'],['/pharmacy-register','Partner with Us']]},
          ].map(col=>(
            <div key={col.title}>
              <div style={{fontSize:'.65rem',letterSpacing:'.16em',textTransform:'uppercase',color:C.g400,marginBottom:'1.25rem',fontWeight:600}}>{col.title}</div>
              {col.links.map(([to,label])=>(
                <Link key={label} to={to} style={{display:'block',fontSize:'.83rem',color:'rgba(255,255,255,.45)',textDecoration:'none',marginBottom:'.65rem',transition:'color .2s'}}
                  onMouseEnter={e=>e.currentTarget.style.color=C.g300}
                  onMouseLeave={e=>e.currentTarget.style.color='rgba(255,255,255,.45)'}>
                  {label}
                </Link>
              ))}
            </div>
          ))}
        </div>
        <div style={{borderTop:'1px solid rgba(255,255,255,.07)',paddingTop:'1.75rem',display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:'.75rem',flexWrap:'wrap',gap:'.75rem'}}>
          <span>© {new Date().getFullYear()} Revive Skin & Hair Experts. All rights reserved.</span>
          <div style={{display:'flex',gap:'1.75rem'}}>
            {[['Privacy Policy','/privacy-policy'],['Terms of Service','/terms-of-service'],['Refund Policy','/refund-policy'],['Delete My Data','/delete-account']].map(([l,to])=>(
              <Link key={l} to={to} style={{color:'rgba(255,255,255,.35)',textDecoration:'none',transition:'color .2s'}}
                onMouseEnter={e=>e.currentTarget.style.color='rgba(255,255,255,.7)'}
                onMouseLeave={e=>e.currentTarget.style.color='rgba(255,255,255,.35)'}>{l}</Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  )
}

